import { FC, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  Container,
  IconButton,
  TextField,
} from '@material-ui/core';
import '../index.scss'
import { Row } from 'components/Row';
import CloseIconModal from 'assets/icons/CloseIconModal';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal, openModal } from 'store/modules/ModalActions';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { schemaAnoImplementacao } from 'pages/Escola/Cadastro/etapas/ProgramasEducacionais/schema';
import { TipoPrograma } from 'store/modules/EscolasTypes';
import { RootState } from 'store';
import languageValues from 'utils/LanguageUtils';
import ModalAviso from '../ModalAviso';
import useProgramas from '../../../hooks/useProgramas';
import { updateProgramaValue } from 'store/modules/EscolasActions';
interface ModalInputAnoProps {
  title: string;
  cancelCallback?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmThemeButton?: string;
  cancelThemeButton?: string;
  idEscola: string;
  modalKey: string;
  programaValue: TipoPrograma;
}

const ModalInputAno: FC<ModalInputAnoProps> = ({
  modalKey,
  idEscola,
  title,
  cancelCallback,
  confirmLabel,
  cancelLabel,
  programaValue,
  confirmThemeButton,
  cancelThemeButton,
}) => {
  const {
    register,
    watch,
    reset,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(schemaAnoImplementacao),
    mode: 'onChange',
  });
  const { getNomePrograma } = useProgramas();
  const programas = useSelector((state: RootState) => state.escolas.programas);
  const dispatch = useDispatch();

  const watchedAnoImplementacao = watch(
    'anoImplementacao',
    programas[programaValue]?.anoImplementacao || ''
  );

  const toClose = useCallback(() => {
    dispatch(closeModal(modalKey));
  }, [dispatch, modalKey]);

  const toCancel = useCallback(() => {
    if (typeof cancelCallback === 'function') {
      cancelCallback();
    }
    toClose();
  }, [cancelCallback, toClose]);

  useEffect(() => {
    reset({ anoImplementacao: '' });
  }, [modalKey, programaValue, programas, reset]);

  return (
    <Dialog
      className='GenericModal'
      open={true}
      onClose={toCancel}
      closeAfterTransition
      disableEscapeKeyDown
      fullScreen
    >
      <Box>
        <DialogTitle className='GenericModal_dialogTitle'>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography className='GenericModal_titleText'>
              {title}
            </Typography>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                toCancel();
              }}
              size="small"
            >
              <CloseIconModal style={{ width: 'auto', height: '22px' }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <form>
            <TextField
              {...register('anoImplementacao')}
              placeholder="Digite o ano de implementação do programa"
              className='form__input-ano'
              label={languageValues.pages.escolas.labels.AnoImplementacao}
              type='text'
              inputProps={{ maxLength: 4, pattern: '[0-9]*' }}
              InputLabelProps={{ shrink: true }}
              error={!!errors.anoImplementacao}
              helperText={
                <div className='form__helperText'>
                  <Typography
                    className='form__label'
                    color={errors.anoImplementacao ? 'error' : 'initial'}
                  >
                    {errors.anoImplementacao
                      ? errors.anoImplementacao.message
                      : ''}
                  </Typography>
                </div>
              }
            />
          </form>
        </DialogContent>

        <Container className='GenericModal_dialogActions'>
          <Row className='GenericModal_row'>
            <Box
              className='GenericModal_box-buttons'
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                className={`GenericModal_action-button ${cancelThemeButton || ''}`}
                variant='contained'
                onClick={(e) => {
                  e.stopPropagation();
                  cancelCallback?.();
                  dispatch(closeModal(modalKey));
                }}
              >
                {cancelLabel || 'Cancelar'}
              </Button>

              <Button
                className={`GenericModal_action-button ${confirmThemeButton || ''}`}
                variant='contained'
                onClick={() => {
                  if (watchedAnoImplementacao) {
                    dispatch(updateProgramaValue(programaValue, { anoImplementacao: watchedAnoImplementacao }));
                    dispatch(openModal(ModalAviso, {
                      modalKey: 'modalSubmit',
                      idEscola,
                      title: `Aviso - ${getNomePrograma(programaValue)}`,
                      tipoPrograma: programaValue,
                      ano: watchedAnoImplementacao,
                      confirmThemeButton: 'button_PUFV',
                      cancelThemeButton: 'button_PUFV',
                      cancelCallback: () => {
                        dispatch(updateProgramaValue(programaValue, { anoImplementacao: "" }));
                      },
                    }));
                  }
                }}

                disabled={!isValid}
              >
                {confirmLabel || 'Confirmar'}
              </Button>
            </Box>
          </Row>
        </Container>
      </Box>
    </Dialog>
  );
};

export default ModalInputAno;